/**
 * WatchAdButton — mobile-only
 *
 * Renders in one of 3 states:
 *  idle       → "Watch Ad → +{N} chips"                   (blue)
 *  capReached → "You've watched all 10 ads for this hour" (grey, disabled)
 *  loading    → spinner  (ad loading / playing / auto-claiming)
 *
 * Note: reward is claimed automatically via polling after the ad finishes.
 * The "+N chips!" celebration is shown via a Toast notification.
 */
import React from "react";
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useAdReward } from "../../../utils/hooks/useAdReward";

interface Props {
  /** Compact style for embedding inside the buy-chips sheet */
  compact?: boolean;
}

const WatchAdButton: React.FC<Props> = ({ compact = false }) => {
  // Mobile only
  if (Platform.OS === "web") return null;

  return <WatchAdButtonInner compact={compact} />;
};

const WatchAdButtonInner: React.FC<Props> = ({ compact }) => {
  const {
    buttonState,
    chips,
    hourlyCounterLabel,
    handlePress,
  } = useAdReward();

  const isDisabled =
    buttonState === "capReached" ||
    buttonState === "loading";

  const getButtonColor = () => {
    switch (buttonState) {
      case "capReached":
        return "#555";
      default:
        return "#1a78c2";
    }
  };

  const getLabel = () => {
    switch (buttonState) {
      case "idle":
        return `Watch Ad +${chips || 100} chips`;
      case "capReached":
        return `You've watched all 10 ads for this hour. Come back later!`;
      case "loading":
        return "";
    }
  };

  return (
    <View>
      <Text style={[styles.counter, compact && styles.counterCompact]}>
        {hourlyCounterLabel}
      </Text>
      <TouchableOpacity
        style={[
          styles.button,
          compact && styles.buttonCompact,
          { backgroundColor: getButtonColor() },
          isDisabled && styles.buttonDisabled,
        ]}
        onPress={handlePress}
        disabled={isDisabled}
        activeOpacity={0.85}
      >
        {buttonState === "loading" ? (
          <ActivityIndicator color="#fff" size="small" />
        ) : (
          <Text style={[styles.label, compact && styles.labelCompact]}>
            {getLabel()}
          </Text>
        )}
      </TouchableOpacity>
    </View>
  );
};

export default WatchAdButton;

const styles = StyleSheet.create({
  counter: {
    color: "#aaa",
    fontSize: 13,
    textAlign: "center",
    marginBottom: 6,
  },
  counterCompact: {
    fontSize: 11,
    marginBottom: 4,
  },
  button: {
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 24,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 48,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 4,
  },
  buttonCompact: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    minHeight: 40,
    borderRadius: 8,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  label: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 16,
    textAlign: "center",
    textShadowColor: "rgba(0,0,0,0.4)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  labelCompact: {
    fontSize: 14,
  },
});
